/* eslint-disable prettier/prettier */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { Formik } from 'formik';
import moment from 'moment';
import { ThemeProps, withTheme } from 'styled-components';
import * as yup from 'yup';

import AppBar from 'components/AppBar';
import ConfirmBottomSheet from 'components/ConfirmBottomSheet';
import ImagePicker from 'components/ImagePicker';
import Loader from 'components/Loader';
import { Desktop, Mobile } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { TravelType } from 'models/enums/travelType';
import { Expense as ExpenseModel, ExpenseForm } from 'models/expense';
import { TravelDetails } from 'models/travelDetails';
import ExpensesSuccess from 'screens/components/ExpensesSuccess';
import { generateReceipt } from 'services/receiptService';
import { base64ToFile } from 'utils/generic';

import ExpenseValues from './components/ExpenseValues';
import { Scaffold, Title, Subtitle, Form, ButtonsContainer, ConfirmBtn, DeleteBtn } from './styles';

type SetFieldValue = (field: string, value: unknown) => void;
type SetFieldTouched = (field: string) => void;

interface Props extends ThemeProps<any> {
  saving: boolean;
  loading: boolean;
  expense?: ExpenseModel;
  travel?: TravelDetails;
  onDelete: (expenseId: string) => void;
  onSave: (data: ExpenseForm) => void;
  showSuccess: boolean;
  successTravelId?: string;
}

const Expense = ({
  theme,
  saving,
  loading,
  expense,
  travel,
  onSave,
  onDelete,
  showSuccess,
  successTravelId,
}: Props) => {
  const history = useHistory();
  const { t } = useStoreTranslation();

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [importFromRappiReceiptImage, setImportFromRappiReceiptImage] = useState<File>();

  useEffect(() => {
    const generateImage = async (expense: ExpenseModel) => {
      if (expense.rappiOrder) {
        const dataURL = await generateReceipt(
          {
            type: 'Others',
            name: expense.rappiOrder.name,
            date: expense.rappiOrder.createdAt,
            currencyCode: expense.currencyCode,
            subtotal: expense.rappiOrder.totalValue + expense.rappiOrder.discount,
            discounts: expense.rappiOrder.discount,
            total: expense.rappiOrder.totalValue,
          },
          theme.receipt,
          t('locale'),
        );
        setImportFromRappiReceiptImage(await base64ToFile(dataURL, 'png'));
      }
    };
    if (expense && expense.rappiOrderId) {
      generateImage(expense);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expense]);

  const splitDate = (date: Date) => date.toString().split('T').first();

  const formatDate = (date: Date) => {
    const dateSplitted = splitDate(date);
    const newDate = moment(dateSplitted).format();
    return newDate && moment(newDate).toDate();
  };

  const travelInfo = useMemo(
    () =>
      travel?.flightSectors
        ? {
            city: t('expense.flightSubtitle', {
              subtitle: travel?.flightSectors?.last().flights.first().departureCityName,
            }),
            date: travel?.flightSectors?.first().flights.first().departureDate,
          }
        : travel?.type === TravelType.Vehicle
        ? {
            city: t('expense.vehicleSubtitle', { subtitle: travel?.vehicle?.cityName }),
            date: travel?.vehicle?.pickupDateTime,
          }
        : travel?.type === TravelType.ExtraCharge
        ? {
            city: t('expense.extraChargeSubtitle', { subtitle: travel?.hotel?.cityName }),
            date: travel?.hotel?.checkIn,
          }
        : {
            city: t('expense.hotelSubtitle', { subtitle: travel?.hotel?.cityName }),
            date: travel?.hotel?.checkIn,
          },
    [t, travel],
  );

  const initialValues: ExpenseForm = {
    file: (expense?.file as File) || '',
    fileExtension: expense?.fileExtension || '',
    fileUpdated: false,
    value: expense?.value || '',
    date: expense ? formatDate(expense.date) : '',
    name: expense?.name || '',
    type: expense?.type || '',
    chargeCode: expense?.chargeCode || '',
    comments: expense?.comments || '',
  };

  const validationSchema = yup.object().shape({
    file: yup.string(),
    fileExtension: yup.string().when('file', {
      is: (file) => file,
      then: yup.string().required(),
    }),
    fileUpdated: yup.bool().required(),
    value: yup.number().min(1).max(999999.99, t('expense.values.value.maxLenghtMessage')).required(),
    date: yup.string().required(),
    name: yup.string(),
    type: yup.string().required(),
    chargeCode: yup.number(),
    comments: yup.string(),
  });

  const getTravelTitle = useCallback(
    () => `${travelInfo.city} | ${moment(travelInfo.date).format('DD MMM')}`,
    [travelInfo],
  );

  const getBottomSheetContent = () => {
    const formattedDate = moment(travelInfo.date).format(t('expense.delete.dateFormat'));

    return t('expense.delete.content', { city: travelInfo.city, date: formattedDate });
  };

  const handleClose = () => {
    history.goBack();
  };

  const handleSubmit = (data: ExpenseForm) => {
    onSave(data);
  };

  const handleDelete = () => {
    setShowDeleteConfirmation(true);
  };

  const handleImagePickerChange = async (
    setFieldValue: SetFieldValue,
    setFieldTouched: SetFieldTouched,
    file?: File,
    type?: string,
  ) => {
    await setFieldValue('file', file || '');
    await setFieldValue('fileExtension', type?.split('/')[1] || '');
    await setFieldValue('fileUpdated', true);
    setFieldTouched('file');
    setFieldTouched('fileExtension');
  };

  const title = t(!expense ? 'expense.createTitle' : 'expense.editTitle');
  const subtitle = travel && getTravelTitle();

  return (
    <>
      <Desktop>
        <ScreenHeader title={title} subtitle={subtitle} />
      </Desktop>

      <Mobile>
        <AppBar goBack={handleClose}>
          <Title>{title}</Title>
          <Subtitle>{subtitle}</Subtitle>
        </AppBar>
      </Mobile>

      <Scaffold>
        {loading ? (
          <Loader fullScreen />
        ) : (
          <Formik
            isInitialValid={false}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isValid, values, handleSubmit: submit, setFieldValue, setFieldTouched }) => (
              <Form>
                <ExpenseValues />
                <Form.Title>{t('expense.image.title')}</Form.Title>
                <ImagePicker
                  deleteFileText={!expense?.rappiOrderId ? t('expense.image.delete') : undefined}
                  currentFile={!expense?.rappiOrderId ? (values.file as File) : importFromRappiReceiptImage}
                  onChange={(file?: File, type?: string) => {
                    handleImagePickerChange(setFieldValue, setFieldTouched, file, type);
                  }}
                />
                <ButtonsContainer>
                  <ConfirmBtn
                    value={t('expense.values.save')}
                    disabled={!isValid || saving}
                    loading={saving}
                    onClick={submit}
                  />
                  {expense?.id && <DeleteBtn onClick={handleDelete}>{t('expense.delete.button')}</DeleteBtn>}
                </ButtonsContainer>
              </Form>
            )}
          </Formik>
        )}
      </Scaffold>
      <ConfirmBottomSheet
        red
        title={t('expense.delete.title')}
        show={showDeleteConfirmation}
        content={getBottomSheetContent()}
        confirmText={t('expense.delete.bottomSheetButton')}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={() => onDelete(expense?.id || '')}
      />
      <ExpensesSuccess show={showSuccess} travelId={successTravelId} />
    </>
  );
};

export default withTheme(Expense);
